"use client";

import { createContext, useContext, useEffect, useRef, useState, useCallback, ReactNode } from "react";
import { usePathname } from "next/navigation";
import { useMotionMode } from "./MotionContext";
import * as spatialAudio from "@/lib/audio/spatialAudio";

interface AudioContextValue {
  isInitialized: boolean;
  isMuted: boolean;
  setMuted: (muted: boolean) => void;
}

const AudioContext = createContext<AudioContextValue>({
  isInitialized: false,
  isMuted: false,
  setMuted: () => {},
});

export function useAudio() {
  return useContext(AudioContext);
}

export function AudioProvider({ children }: { children: ReactNode }) {
  const { gpuTier } = useMotionMode();
  const [isInitialized, setIsInitialized] = useState(false);
  const [isMuted, setIsMuted] = useState(() => spatialAudio.getMute());
  const initRef = useRef(false);
  const pathname = usePathname();

  // Experiments and micro-apps own their own audio — suspend the portfolio's
  // spatial audio so the two systems don't stack.
  const isExpRoute = pathname.startsWith("/exp") || pathname.startsWith("/apps");

  // Sync GPU tier to audio system
  useEffect(() => {
    spatialAudio.setGpuTier(gpuTier);
  }, [gpuTier]);

  // Initialize audio on first user interaction (required by browsers)
  useEffect(() => {
    // Don't init portfolio audio if we're already on an experiment page
    if (isExpRoute) return;

    const initOnInteraction = async () => {
      if (initRef.current) return;
      initRef.current = true;
      await spatialAudio.initAudio();
      // Start ambient layers after audio context is ready
      spatialAudio.startAmbient();
      spatialAudio.startAmbientBlips();
      setIsInitialized(true);
    };

    window.addEventListener("click", initOnInteraction, { once: true });
    window.addEventListener("touchstart", initOnInteraction, { once: true });
    window.addEventListener("keydown", initOnInteraction, { once: true });

    return () => {
      window.removeEventListener("click", initOnInteraction);
      window.removeEventListener("touchstart", initOnInteraction);
      window.removeEventListener("keydown", initOnInteraction);
    };
  }, [isExpRoute]);

  // Suspend/resume portfolio audio when entering/leaving experiment routes
  useEffect(() => {
    if (!initRef.current) return; // not initialized yet, nothing to suspend

    if (isExpRoute) {
      spatialAudio.stopAmbient();
      spatialAudio.stopAmbientBlips();
      spatialAudio.stopEmergence();
    } else {
      spatialAudio.startAmbient();
      spatialAudio.startAmbientBlips();
    }
  }, [isExpRoute]);

  const setMuted = useCallback((muted: boolean) => {
    spatialAudio.setMute(muted);
    setIsMuted(muted);
  }, []);

  return (
    <AudioContext.Provider value={{ isInitialized, isMuted, setMuted }}>
      {children}
    </AudioContext.Provider>
  );
}
